import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosWithAuth';
import { useAuth } from '../context/AuthContext';
import styles from './AdminDashboard.module.css';
import { getDatabase, ref, onValue } from 'firebase/database';
import { Link, useNavigate } from 'react-router-dom';
import Pagination from './Pagination';
import SubscribersControl from '../components/SubscribersControl';
import { DateTime } from 'luxon';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [jobs, setJobs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [expandedJobId, setExpandedJobId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 10;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const isAdmin = currentUser?.email === 'aubair245@gmail.com';

  // 🔐 Helper to get auth header
  const getAuthHeader = async () => {
    const token = await currentUser?.getIdToken();
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // 📊 Email stats
 useEffect(() => {
  axios.get(`${process.env.REACT_APP_API_URL}/api/email/stats`)
    .then(res => setSummary(res.data))
    .catch(err => console.error('Failed to fetch email stats', err));
}, []);


  // 📧 Subscribers
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/subscribers`)

      .then(res => setSubscriberCount(res.data.length))
      .catch(err => {
        console.error("Failed to fetch subscribers", err);
        setSubscriberCount(0);
      });
  }, []);

  // 📦 Counts
  useEffect(() => { fetchJobCounts(); }, []);
  useEffect(() => { fetchJobsByTab(); }, [activeTab, currentPage]);
  useEffect(() => { listenToOnlineUsers(); }, []);

  const fetchJobCounts = async () => {
    try {
      const [pending, approved, rejected, archived] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/pending`),
    axios.get(`${process.env.REACT_APP_API_URL}/api/jobs`),
    axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/rejected`),
    axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/archived`),

      ]);
      setPendingCount(pending.data.total || 0);
      setApprovedCount(approved.data.total || 0);
      setRejectedCount(rejected.data.total || 0);
      setArchivedCount(archived.data.total || 0);
    } catch (err) {
      console.error('Error fetching job counts:', err);
    }
  };

  const fetchJobsByTab = async () => {
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'pending': endpoint = 'pending'; break;
        case 'approved': endpoint = ''; break;
        case 'rejected': endpoint = 'rejected'; break;
        case 'archived': endpoint = 'archived'; break;
        default: return;
      }
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${endpoint}?page=${currentPage}&limit=${jobsPerPage}`);
      setJobs(res.data.jobs || []);
      setTotalCount(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

 const listenToOnlineUsers = () => {
  const db = getDatabase();
  const usersRef = ref(db, 'onlineUsers');

  onValue(usersRef, (snapshot) => {
    const data = snapshot.val();
    const now = new Date();

    const activeUsers = Object.values(data || {}).filter((user) => {
      if (!user.lastActive) return false;
      const lastActive = new Date(user.lastActive);
      return now - lastActive < 2 * 60 * 1000; // 2 minutes = 120000 ms
    });

    setOnlineUsers(activeUsers);
  });
};


 // 🔐 Protected Actions
const approveJob = async (id) => {
  try {
    const config = await getAuthHeader();
    await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/approve/${id}`, {}, config);
    alert('✅ Job approved successfully!');
    fetchJobsByTab();
    fetchJobCounts();
  } catch (err) {
    alert('❌ Failed to approve job.');
    console.error(err);
  }
};

const rejectJob = async (id) => {
  try {
    const config = await getAuthHeader();
    await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/reject/${id}`, {}, config);
    alert('🚫 Job rejected successfully!');
    fetchJobsByTab();
    fetchJobCounts();
  } catch (err) {
    alert('❌ Failed to reject job.');
    console.error(err);
  }
};

const archiveJob = async (id) => {
  if (window.confirm('Archive this job?')) {
    try {
      const config = await getAuthHeader();
      await axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/archive/${id}`, {}, config);
      alert('📦 Job archived successfully!');
      fetchJobsByTab();
      fetchJobCounts();
    } catch (err) {
      alert('❌ Failed to archive job.');
      console.error(err);
    }
  }
};

const deleteJob = async (id) => {
  if (window.confirm('Delete permanently?')) {
    try {
      const config = await getAuthHeader();
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/jobs/${id}`, config);
      alert('✅ Job deleted successfully');
      fetchJobsByTab();
      fetchJobCounts();
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('❌ Failed to delete job');
    }
  }
};

 // ✅ Paste this here:
  const handlePendingJobStats = () => {
    alert("Pending job stats by date will be shown here.");
    // Later: navigate, fetch or open modal here
  };

const filteredJobs = jobs.filter((job) => {
const matchesSearch = [
  job.jobHeading,
  job.company,
  job.location,
  ...(Array.isArray(job.categories) ? job.categories : [])
].some((field) =>
  String(field || '').toLowerCase().includes(searchQuery.toLowerCase())
);


  const matchesDate = selectedDate
    ? DateTime.fromISO(job.createdAt).toISODate() === selectedDate
    : true;

  return matchesSearch && matchesDate;
});

const renderJobCard = (job, index, isPending = false) => {
  const isExpanded = expandedJobId === job._id;

  return (
    <div
      key={job._id}
      className={`${styles.jobCard} ${!isPending && job.status === 'rejected' ? styles.rejected : ''}`}
    >
      <div
  className={styles.jobHeader}
  style={{
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: '8px',
    marginBottom: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }}
>
  <span
    style={{
      fontWeight: 'bold',
      fontSize: '1.05rem',
      color: '#0d6efd',
      flexShrink: 0
    }}
  >
    #{job.jobNumber || index + 1}
  </span>
  <h3
    onClick={() => setExpandedJobId(isExpanded ? null : job._id)}
    style={{
      cursor: 'pointer',
      color: '#0d6efd',
      textDecoration: 'underline',
      marginBottom: 0,
      fontSize: '1.05rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}
    title={Array.isArray(job.title) ? job.title.join(', ') : job.title}
  >
    {Array.isArray(job.title) ? job.title.join(', ') : job.title || job.jobHeading || 'Untitled Job'}
    {isExpanded ? ' 🔼' : ' 🔽'}
  </h3>
</div>


      {isExpanded && (
  <div
    className={styles.jobContent}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '20px',
    }}
  >
    {/* LEFT: Job Text Info */}
    <div
      style={{
        flex: '1 1 60%',
        minWidth: '250px',
      }}
    >
      <div style={{ whiteSpace: 'pre-wrap', marginBottom: '10px' }}>
        <p><strong>Job Details:</strong></p>
        <div
  style={{ whiteSpace: 'pre-wrap' }}
  dangerouslySetInnerHTML={{
    __html: String(job.jobDetails || '')
      // Remove any unwanted <button> or Apply Now / Download text
      .replace(/<button[^>]*>.*?<\/button>/gi, '')
      .replace(/Apply\s*Now/gi, '')
      .replace(/Download\s*Notification/gi, '')
      // Convert URLs into clickable links
      .replace(
        /(https?:\/\/[^\s]+|www\.[^\s]+)/gi,
        (url) => {
          let href = url;
          if (!href.startsWith('http')) {
            href = 'https://' + href;
          }
          return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
        }
      )
  }}
></div>

      </div>

      {job.company && (
        <p><strong>Company:</strong> {Array.isArray(job.company) ? job.company.join(', ') : job.company}</p>
      )}

      <p><strong>Location:</strong> {Array.isArray(job.location) ? job.location.join(', ') : job.location || 'N/A'}</p>
{Array.isArray(job.categories) && job.categories.length > 0 && (
  <div style={{ marginBottom: '8px' }}>
    <strong>Categories:</strong>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
      {job.categories.map((cat, idx) => (
        <span
          key={idx}
          style={{
            background: '#e7f1ff',
            color: '#0d6efd',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          📌 {cat}
        </span>
      ))}
    </div>
  </div>
)}



      {job.email && (
        <p><strong>Email:</strong> {Array.isArray(job.email) ? job.email.join(', ') : job.email}</p>
      )}

      <p><strong>Contact Number:</strong> {Array.isArray(job.contact) ? job.contact.join(', ') : job.contact || 'N/A'}</p>

  
{job.pdfUrl && (
  <div
    style={{
      marginTop: '8px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      justifyContent: window.innerWidth <= 768 ? 'center' : 'flex-start' // ⬅ desktop left, mobile center
    }}
  >
    <a
      href={job.pdfUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        padding: '6px 10px',
        background: '#0d6efd',
        color: '#fff',
        borderRadius: '4px',
        textDecoration: 'none'
      }}
    >
      📄 View PDF
    </a>

    <a
      href={job.pdfUrl}
      download={`job-pdf-${job._id}.pdf`}
      style={{
        padding: '6px 10px',
        background: '#198754',
        color: '#fff',
        borderRadius: '4px',
        textDecoration: 'none'
      }}
    >
      ⬇ Download PDF
    </a>
  </div>
)}



      <p><strong>Date:</strong> {DateTime.fromISO(job.createdAt).setZone('Asia/Kolkata').toFormat('dd LLLL yyyy, hh:mm a')}</p>
    </div>

    {/* RIGHT: Poster Image */}
    {job.imageUrl && (
      <div
        style={{
          flex: '1 1 35%',
          textAlign: 'center',
          minWidth: '200px',
        }}
      >
        <img
          src={job.imageUrl.replace('/upload/', '/upload/w_400,h_300,c_fill/')}
          alt="Job Poster"
          className={styles.posterImage}
          style={{ maxWidth: '100%', borderRadius: '8px' }}
          onError={(e) => { e.target.src = '/default-placeholder.png'; }}
        />
        <a
          href={job.imageUrl}
          target="_blank"
          rel="noopener noreferrer"
          download={`job-poster-${job._id}.jpg`}
          className={styles.downloadBtn}
          style={{
            display: 'inline-block',
            marginTop: '10px',
            padding: '8px 12px',
            backgroundColor: '#0d6efd',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '5px'
          }}
        >
          ⬇ Download Poster
        </a>
      </div>
    )}
  </div>
)}

<div
  className="jobCardWrapper"
  style={{
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
    padding: '0 16px', // ensures left and right spacing
    boxSizing: 'border-box',
  }}
>
  <div
    style={{
      width: '100%',
      maxWidth: '500px', // Adjust max width for desktop
      background: '#fff',
      textAlign: 'center',
    }}
  >
    {/* Job Heading + Details */}
  </div>
</div>

        
  
          {isAdmin && (
            <div className={styles.actionButtons}>
              {isPending ? (
                <>
                  <button className={styles.approve} onClick={() => approveJob(job._id)}>✅ Approve</button>
                  <button className={styles.reject} onClick={() => rejectJob(job._id)}>❌ Reject</button>
                </>
              ) : (
                <>
                  <button className={styles.edit} onClick={() => navigate(`/admin/job-form/${job._id}`)}>✏️ Edit</button>
                  <button className={styles.archive} onClick={() => archiveJob(job._id)}>📦 Archive</button>
                  <button className={styles.delete} onClick={() => deleteJob(job._id)}>🗑️ Delete</button>
                </>
              )}
            </div>
          )}
        
      
    </div>
  );
};


 
  return (
    <div className={styles.adminDashboard}>
      <h2>Admin Dashboard</h2>

      <div className={styles.dashboardSummary}>
        <div className={`${styles.summaryBox} ${styles.blue}`}>
          <h5>Online Users</h5>
          <p>{onlineUsers.length}</p>
          <Link to="/admin/online-users" className="btn fw-semibold" style={{ backgroundColor: 'white', color: '#0d6efd' }}>
            View Online User Emails
          </Link>
        </div>

{/* Pending Jobs Card with Daily Stats Link */}
<div className={`${styles.summaryBox} ${styles.yellow}`}>
  <h5>Pending Jobs</h5>
  <p>{pendingCount}</p>
 <Link
  to="/admin/job-stats"
  className="btn fw-semibold mt-2"
  style={{ backgroundColor: 'white', color: '#1c4603ff' }}
>
  📊 View Daily Job Count
</Link>

</div>





        <div className={`${styles.summaryBox} ${styles.green}`}>
          <h5>Approved Jobs</h5>
          <p>{approvedCount}</p>
          {approvedCount > 0 && isAdmin && (
            <Link to="/admin/send-notification" className="btn fw-semibold mt-2" style={{ backgroundColor: 'white', color: '#198754' }}>
              📢 Send Notification
            </Link>
          )}
        </div>

        <div className={`${styles.summaryBox} ${styles.red}`}>
          <h5>Mail Center</h5>
          <p>{subscriberCount} Subscribers</p>
          <Link to="/admin/email-dashboard" className="btn fw-semibold mt-2" style={{ backgroundColor: 'white', color: '#dc3545' }}>
            📬 Open Email Dashboard
          </Link>
        </div>

        <div className={`${styles.summaryBox} ${styles.red}`}><h5>Rejected Jobs</h5><p>{rejectedCount}</p></div>
        <div className={`${styles.summaryBox} ${styles.gray}`}><h5>Archived Jobs</h5><p>{archivedCount}</p></div>
      </div>

      <div style={{ width: '100%' }}><SubscribersControl /></div>


      <div className={styles.tabs}>
        {['pending', 'approved', 'rejected', 'archived'].map(tab => (
          <button key={tab} onClick={() => { setActiveTab(tab); setCurrentPage(1); }} className={activeTab === tab ? styles.active : ''}>
            {tab === 'pending' ? '⏲ Pending' : tab === 'approved' ? '✅ Approved' : tab === 'rejected' ? '❌ Rejected' : '📦 Archived'}
          </button>
        ))}
      </div>

      <div className={styles.searchBar}>
  <div className={styles.filterField}>
    <label htmlFor="search" className={styles.filterLabel}>🔍 Search by Title / Company / Location / Category</label>
    <input
      id="search"
      type="text"
      placeholder="e.g. Developer, Google, Delhi, IT"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      className={styles.filterInput}
    />
  </div>

  <div className={styles.filterField}>
    <label htmlFor="dateFilter" className={styles.filterLabel}>📅 Filter by Date</label>
    <input
      id="dateFilter"
      type="date"
      value={selectedDate}
      onChange={(e) => setSelectedDate(e.target.value)}
      className={styles.filterInput}
    />
  </div>
</div>


      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / jobsPerPage)}
        onPageChange={setCurrentPage}
      />

      <div className={styles.jobList}>
        {filteredJobs.map((job, index) =>
          renderJobCard(job, (currentPage - 1) * jobsPerPage + index, activeTab === 'pending')
        )}
        {filteredJobs.length === 0 && <p>No {activeTab} jobs found.</p>}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / jobsPerPage)}
        onPageChange={setCurrentPage}
      />

      <div className={styles.goToTopFixed}>
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          ↑ Go To Top
        </button>
      </div>
    </div>
  );
};

export default AdminDashboard;
